/**
 * Copy and paste of block subtrees (spec §8.18, M15). A copy is a typed snapshot of the blocks' Loro
 * data taken at copy time; pasting restores it into any page of the workspace in one of three modes:
 * `copy` (independent blocks, new identities, internal links re-pointed), `mirror` (one `mirror` block
 * per root that shows the original live, §7.4.13) or `empty` (structure and titles only).
 */
import { isContainer, LoroCounter, LoroList, LoroMap, LoroMovableList, LoroText, LoroTree, type Container, type Delta, type LoroDoc, type LoroTreeNode, type TreeID } from "loro-crdt";
import type { BlockType, MirrorSource } from "@kss/schema";
import { v7 as uuidv7 } from "uuid";
import { pmSchema } from "../../editors/pmSchema";
import { blockIri, docIri } from "../../lib/iri";
import { blocksTree } from "../model";
import { blockDef } from "../registry";

/** A typed snapshot of a Loro value: containers keep their kind, texts their marks. */
export type Snap =
  | { t: "map"; v: Record<string, Snap> }
  | { t: "text"; delta: Delta<string>[] }
  | { t: "list" | "mlist"; v: Snap[] }
  | { t: "tree"; roots: TreeSnap[] }
  | { t: "counter"; v: number }
  | { t: "value"; v: unknown };

export interface TreeSnap {
  id: string;
  data: Snap;
  children: TreeSnap[];
}

export interface BlockSnap {
  docId: string;
  treeId: string;
  type: BlockType;
  /** the node's data map: `type`, `createdAt`, `createdBy`, `body`, `links`, `annotations`, `groups` */
  data: Extract<Snap, { t: "map" }>;
  children: BlockSnap[];
}

export type PasteMode = "copy" | "mirror" | "empty";

// --- snapshot ------------------------------------------------------------------------------------

export function snapValue(value: unknown): Snap {
  if (!isContainer(value)) return { t: "value", v: structuredClone(value) };
  const c = value as Container;
  switch (c.kind()) {
    case "Map": {
      const m = c as LoroMap;
      const v: Record<string, Snap> = {};
      for (const k of m.keys()) v[k] = snapValue(m.get(k));
      return { t: "map", v };
    }
    case "Text":
      return { t: "text", delta: (c as LoroText).toDelta() };
    case "List":
    case "MovableList": {
      const l = c as LoroList | LoroMovableList;
      const v: Snap[] = [];
      for (let i = 0; i < l.length; i++) v.push(snapValue(l.get(i)));
      return { t: c.kind() === "List" ? "list" : "mlist", v };
    }
    case "Tree": {
      const walk = (n: LoroTreeNode): TreeSnap => ({ id: n.id, data: snapValue(n.data), children: (n.children() ?? []).map(walk) });
      return { t: "tree", roots: (c as LoroTree).roots().map(walk) };
    }
    case "Counter":
      return { t: "counter", v: (c as LoroCounter).value };
    default:
      return { t: "value", v: null };
  }
}

export function snapBlock(docId: string, node: LoroTreeNode): BlockSnap {
  return {
    docId,
    treeId: node.id,
    type: (node.data.get("type") as BlockType | undefined) ?? "section",
    data: snapValue(node.data) as Extract<Snap, { t: "map" }>,
    children: (node.children() ?? []).map((n) => snapBlock(docId, n)),
  };
}

/** Selected blocks → the roots to copy: a block whose ancestor is selected comes along with it; page order. */
export function normaliseSelection(doc: LoroDoc, ids: Iterable<string>): TreeID[] {
  const tree = blocksTree(doc);
  const wanted = new Set(ids);
  const out: TreeID[] = [];
  const walk = (nodes: LoroTreeNode[] | undefined) => {
    for (const n of nodes ?? []) {
      if (wanted.has(n.id)) out.push(n.id);
      else walk(n.children());
    }
  };
  walk(tree.roots());
  return out;
}

// --- restore ---------------------------------------------------------------------------------------

function getLoroTextStyle(): Record<string, { expand: "after" | "none" }> {
  // loro-prosemirror's own rule (not exported): marks expand after unless declared non-inclusive
  return Object.fromEntries(Object.entries(pmSchema.marks).map(([name, type]) => [name, { expand: type.spec.inclusive !== false ? "after" : "none" }]));
}

const containerFor = (snap: Snap): Container | null => {
  switch (snap.t) {
    case "map":
      return new LoroMap();
    case "text":
      return new LoroText();
    case "list":
      return new LoroList();
    case "mlist":
      return new LoroMovableList();
    case "tree":
      return new LoroTree();
    case "counter":
      return new LoroCounter();
    default:
      return null;
  }
};

/** Fill an attached container from a snapshot of the same kind. `onTreeNode` sees old → new tree node ids. */
function fill(c: Container, snap: Snap, onTreeNode?: (oldId: string, newId: TreeID) => void): void {
  switch (snap.t) {
    case "map":
      for (const [k, v] of Object.entries(snap.v)) setIn(c as LoroMap, k, v, onTreeNode);
      return;
    case "text":
      if (snap.delta.length) (c as LoroText).applyDelta(snap.delta);
      return;
    case "list":
    case "mlist": {
      const l = c as LoroList | LoroMovableList;
      for (const item of snap.v) {
        const child = containerFor(item);
        if (child) fill(l.pushContainer(child), item, onTreeNode);
        else l.push((item as { v: unknown }).v);
      }
      return;
    }
    case "tree": {
      const tree = c as LoroTree;
      tree.enableFractionalIndex(0);
      const add = (n: TreeSnap, parent?: TreeID) => {
        const node = tree.createNode(parent);
        onTreeNode?.(n.id, node.id);
        fill(node.data, n.data, onTreeNode);
        n.children.forEach((ch) => add(ch, node.id));
      };
      snap.roots.forEach((r) => add(r));
      return;
    }
    case "counter":
      (c as LoroCounter).increment(snap.v);
      return;
  }
}

function setIn(map: LoroMap, key: string, snap: Snap, onTreeNode?: (oldId: string, newId: TreeID) => void): void {
  const child = containerFor(snap);
  if (child) fill(map.setContainer(key, child), snap, onTreeNode);
  else map.set(key, (snap as { v: unknown }).v);
}

const textOf = (snap: Snap | undefined): string => (snap?.t === "text" ? snap.delta.map((d) => ("insert" in d ? d.insert : "")).join("") : "");

/** Resolves a mirror's original to snapshots of its current blocks, or null when unavailable. */
export type MirrorResolver = (source: MirrorSource) => Promise<BlockSnap[] | null>;

export function mirrorSourceOf(block: BlockSnap): MirrorSource | null {
  if (block.type !== "mirror") return null;
  const body = block.data.v.body;
  const source = body?.t === "map" ? body.v.source : undefined;
  return source?.t === "value" ? ((source.v as MirrorSource | null) ?? null) : null;
}

/**
 * Replace every mirror in the snapshot by its original's current blocks (copy and empty modes), so a
 * pasted copy never depends on another page. Unresolvable mirrors stay mirrors; nesting stops at `depth`.
 */
export async function resolveMirrors(blocks: BlockSnap[], resolve: MirrorResolver, depth = 4): Promise<BlockSnap[]> {
  const out: BlockSnap[] = [];
  for (const b of blocks) {
    const source = mirrorSourceOf(b);
    if (source && depth > 0) {
      const original = await resolve(source);
      if (original) {
        out.push(...(await resolveMirrors(original, resolve, depth - 1)));
        continue;
      }
    }
    out.push({ ...b, children: await resolveMirrors(b.children, resolve, depth) });
  }
  return out;
}

export interface PasteTarget {
  docId: string;
  parent?: TreeID;
  /** sibling index to insert at; appended when absent */
  index?: number;
  /** a block removed in the same commit (Detach replaces the mirror by the copy) */
  replace?: TreeID;
}

export interface PasteResult {
  roots: TreeID[];
}

/** Why a mirror paste is refused, or null when allowed (§8.18). */
/** Why mirroring is refused here, as an i18n key (spec §8.23), or null when it is allowed. */
export function mirrorRefusal(doc: LoroDoc, sources: MirrorSource[], target: PasteTarget): "paste.refuse.page" | "paste.refuse.block" | null {
  const tree = blocksTree(doc);
  const chain = new Set<string>();
  for (let id: TreeID | undefined = target.parent; id; id = tree.getNodeByID(id)?.parent()?.id) chain.add(id);
  for (const s of sources) {
    if (s.kind === "page" && s.iri === docIri(target.docId)) return "paste.refuse.page";
    if (s.kind === "block") {
      const m = /^urn:km:blk:([^:]+):(.+)$/.exec(s.iri);
      if (m && m[1] === target.docId && chain.has(m[2]!)) return "paste.refuse.block";
    }
  }
  return null;
}

/** The mirror sources for pasting `blocks` as mirrors: a mirror in the set points at its original. */
export function mirrorSources(blocks: BlockSnap[], page: { docId: string } | null): MirrorSource[] {
  if (page) return [{ kind: "page", iri: docIri(page.docId) }];
  return blocks.map((b) => mirrorSourceOf(b) ?? { kind: "block", iri: blockIri(b.docId, b.treeId) });
}

/**
 * Write the blocks into `doc` in one commit. `blocks` must already have mirrors resolved for the copy
 * and empty modes; for `mirror`, pass the sources from `mirrorSources`.
 */
export function pasteBlocks(doc: LoroDoc, userId: string, target: PasteTarget, mode: PasteMode, blocks: BlockSnap[], sources: MirrorSource[] = []): PasteResult {
  const tree = blocksTree(doc);
  tree.enableFractionalIndex(0);
  const now = Date.now();
  const roots: TreeID[] = [];
  const at = (i: number) => (target.index === undefined ? undefined : target.index + i);

  if (mode === "mirror") {
    sources.forEach((source, i) => {
      const node = tree.createNode(target.parent, at(i));
      initCommon(node, "mirror", now, userId).set("source", source);
      roots.push(node.id);
    });
    if (target.replace) tree.delete(target.replace);
    doc.commit();
    return { roots };
  }

  doc.configTextStyle(getLoroTextStyle());
  // phase 1: the block structure, recording old block IRI → new tree id
  const created: Array<{ snap: BlockSnap; node: LoroTreeNode }> = [];
  const blockMap = new Map<string, TreeID>();
  const create = (snap: BlockSnap, parent: TreeID | undefined, index: number | undefined): TreeID => {
    const node = tree.createNode(parent, index);
    blockMap.set(blockIri(snap.docId, snap.treeId), node.id);
    created.push({ snap, node });
    snap.children.forEach((ch) => create(ch, node.id, undefined));
    return node.id;
  };
  blocks.forEach((b, i) => roots.push(create(b, target.parent, at(i))));

  // phase 2: bodies (checklist item ids are renumbered here)
  const itemMap = new Map<string, string>();
  for (const { snap, node } of created) {
    const body = initCommon(node, snap.type, now, userId);
    const bodySnap = snap.data.v.body;
    const oldBlock = blockIri(snap.docId, snap.treeId);
    const onItem = (oldId: string, newId: TreeID) => itemMap.set(`${oldBlock}#item:${oldId}`, `${blockIri(target.docId, node.id)}#item:${newId}`);
    if (mode === "copy") {
      if (bodySnap?.t === "map") for (const [k, v] of Object.entries(bodySnap.v)) setIn(body, k, v, onItem);
      for (const [k, v] of Object.entries(snap.data.v)) if (!COMMON_KEYS.has(k)) setIn(node.data, k, v);
    } else {
      blockDef(snap.type).initBody(body);
      if (bodySnap?.t === "map") keepStructure(snap.type, body, bodySnap.v, now, userId);
      // [M21] how big a block is belongs to the structure being copied, not to the content being
      // emptied (§8.18), so an empty-structure paste keeps the node-level height too
      const h = snap.data.v.height;
      if (h?.t === "value" && typeof h.v === "number") node.data.set("height", h.v);
    }
  }

  // phase 3: links and option groups (copy mode), re-pointed inside the pasted set
  if (mode === "copy") {
    const repoint = (iri: string): string => {
      const direct = blockMap.get(iri);
      if (direct) return blockIri(target.docId, direct);
      return itemMap.get(iri) ?? iri;
    };
    for (const { snap, node } of created) {
      const links = node.data.getOrCreateContainer("links", new LoroMap());
      const linkIds = new Map<string, string>();
      const linksSnap = snap.data.v.links;
      if (linksSnap?.t === "map") {
        for (const [oldId, entry] of Object.entries(linksSnap.v)) {
          if (entry.t !== "map") continue;
          const id = uuidv7();
          linkIds.set(oldId, id);
          const e = links.setContainer(id, new LoroMap());
          for (const [k, v] of Object.entries(entry.v)) {
            if (k === "o" && v.t === "value" && v.v && typeof v.v === "object" && (v.v as { kind?: string }).kind === "block") {
              const o = v.v as { kind: string; value: string };
              e.set("o", { ...o, value: repoint(o.value) });
            } else setIn(e, k, v);
          }
        }
      }
      const groupsSnap = snap.data.v.groups;
      if (groupsSnap?.t === "map" && Object.keys(groupsSnap.v).length) {
        const groups = node.data.getOrCreateContainer("groups", new LoroMap());
        for (const entry of Object.values(groupsSnap.v)) {
          if (entry.t !== "map") continue;
          const g = groups.setContainer(uuidv7(), new LoroMap());
          for (const [k, v] of Object.entries(entry.v)) {
            if ((k === "members" || k === "chosen") && (v.t === "mlist" || v.t === "list")) {
              const list = g.setContainer(k, new LoroMovableList());
              for (const item of v.v) {
                const next = item.t === "value" ? linkIds.get(String(item.v)) : undefined;
                if (next) list.push(next);
              }
            } else setIn(g, k, v);
          }
        }
      }
    }
  }
  if (target.replace) tree.delete(target.replace);
  doc.commit();
  return { roots };
}

/** keys every block node has; restored explicitly, not copied from the snapshot */
const COMMON_KEYS = new Set(["type", "createdAt", "createdBy", "body", "links", "annotations", "groups"]);

function initCommon(node: LoroTreeNode, type: BlockType, now: number, userId: string): LoroMap {
  const data = node.data;
  data.set("type", type);
  data.set("createdAt", now);
  data.set("createdBy", userId);
  data.setContainer("links", new LoroMap());
  data.setContainer("annotations", new LoroMap());
  return data.setContainer("body", new LoroMap());
}

/** Empty structure (§8.18, Q35): keep titles, checklist items (unchecked), code language, graph-view set-up. */
function keepStructure(type: BlockType, body: LoroMap, snap: Record<string, Snap>, now: number, userId: string): void {
  const keepText = (key: string) => {
    const t = textOf(snap[key]);
    if (!t) return;
    const text = body.get(key);
    (text instanceof LoroText ? text : body.setContainer(key, new LoroText())).insert(0, t);
  };
  const keepValue = (key: string) => {
    const v = snap[key];
    if (v?.t === "value") body.set(key, structuredClone(v.v));
  };
  switch (type) {
    case "group":
      keepText("title");
      return;
    case "code":
      keepValue("language");
      return;
    case "graph_view":
      keepText("title");
      keepText("query");
      keepValue("layout");
      if (snap.styleOverrides) setIn(body, "styleOverrides", snap.styleOverrides);
      return;
    case "webpage":
      keepValue("height");
      return;
    case "mirror":
      // an original that could not be resolved: the mirror itself is the structure
      keepValue("source");
      return;
    case "url":
      // how a click behaves is structure; the address is content (spec §7.4.15)
      keepValue("action");
      keepValue("openIn");
      return;
    case "page_link":
      // the reference is structure, not content (spec §7.4.14)
      keepValue("target");
      return;
    case "checklist": {
      keepText("title");
      const items = snap.items;
      if (items?.t !== "tree") return;
      const tree = body.get("items");
      const target = tree instanceof LoroTree ? tree : body.setContainer("items", new LoroTree());
      target.enableFractionalIndex(0);
      const add = (n: TreeSnap, parent?: TreeID) => {
        const node = target.createNode(parent);
        const text = n.data.t === "map" ? textOf(n.data.v.text) : "";
        node.data.setContainer("text", new LoroText()).insert(0, text);
        node.data.set("checked", false);
        node.data.set("createdAt", now);
        node.data.set("createdBy", userId);
        n.children.forEach((ch) => add(ch, node.id));
      };
      items.roots.forEach((r) => add(r));
      return;
    }
  }
}

/** Plain-text outline for the system clipboard: titles and text, indented by depth. */
export function outline(blocks: BlockSnap[]): string {
  const lines: string[] = [];
  const pmText = (snap: Snap | undefined): string => {
    if (!snap) return "";
    if (snap.t === "text") return textOf(snap);
    if (snap.t === "map") return Object.values(snap.v).map(pmText).filter(Boolean).join(snap.v.nodeName ? "\n" : "");
    if (snap.t === "list" || snap.t === "mlist") return snap.v.map(pmText).filter(Boolean).join("\n");
    return "";
  };
  const walk = (bs: BlockSnap[], depth: number) => {
    for (const b of bs) {
      const body = b.data.v.body?.t === "map" ? b.data.v.body.v : {};
      const text = b.type === "section" ? pmText(body.pm) : textOf(body.title) || textOf(body.source) || textOf(body.caption) || (b.type === "mirror" ? `(mirror of ${mirrorSourceOf(b)?.iri ?? "?"})` : `(${b.type})`);
      for (const line of text.split("\n").filter((l) => l.trim())) lines.push(`${"  ".repeat(depth)}${line}`);
      walk(b.children, depth + 1);
    }
  };
  walk(blocks, 0);
  return lines.join("\n");
}
